import { t } from "i18next";
import { Button, Card, Typography, Dialog, DialogHeader, DialogBody, IconButton, Spinner } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { MapIcon, XMarkIcon } from "@heroicons/react/24/outline";

/* Components */
import WorkorderForm, { WorkorderFormValidator } from "../../components/data/workorder/WorkorderForm";
import { compInfoFieldName } from "../../components/data/compinfos/compinfo-errors";

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";

/* Sections */
import { WorkorderMap } from "./sections/WorkorderMap/WorkorderMap";

/* Styles */
import "./CreateWorkorder.scss";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
import { showWorkorderToast } from "./WorkorderToast";
import { useSearchParams } from "react-router-dom";

/**
* CreateWorkorder: Create workorder view 
*/
export function CreateWorkorder() {
  const [workorder, setWorkorder] = useState<EasyTech.Workorder>({} as EasyTech.Workorder);
  const [addressMap, setAddressMap] = useState<EasyTech.Address | undefined>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useSearchParams();
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [unavailableTechs, setUnavailableTechs] = useState<EasyTech.Technician[]>([]);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    if (workorder.address) setAddressMap(workorder.address);
    const start = params.get("start") && params.get("start") !== null ? params.get("start") : "";
    const end = params.get("end") && params.get("end") !== null ? params.get("end") : "";
    if (params && start && end)
      workorder.schedule = {start: start, end: end};
    setParams(undefined);
  }, [workorder, params, setParams]);

  /**
  * useEffect: Set target address on map
  * @param workorder - reload on workorder change 
  */
  useEffect(() => {
    if (workorder.address) setAddressMap(workorder.address);
  }, [workorder]);
  /***/

  /**
  * handleChange: Handle workorder change
  * @param name - key name who changed
  * @param value - new key value
  */
  const handleChange = (name: string, value: any) => {
    setWorkorder((current) => {
      const tmp = { ...current as any };
      if (name === "customer")
        tmp["contacts"] = [];

      if (name === "machines") // Specific machines
        tmp[name] = value.map((el: any) => ({ machine: el, notes: "", parts: [] }));
      else
        tmp[name] = value;
      if (!tmp.compInfos) {
        tmp.compInfos = { inputs: [], selects: []};
      }
      return tmp;
    });
  };
  /***/



  /**
  * handleSubmit: Submit event handler 
  */
  const handleSubmit = async () => {
    if (loading) return;
    setSubmitted(true);

    if (validator.isFormValid(workorder, WorkorderFormValidator)) {
      setLoading(true);
      try {
        const [data] = await Promise.all([
          api.post("users/workorders", workorder),
          new Promise(resolve => setTimeout(resolve, 3000))
        ]);

        const workorderData = data as EasyTech.Workorder;
        
        showWorkorderToast(workorderData.id, workorderData.ref);

        cancel();
      } catch (err) {
        console.error(err);
        showApiErrorToast(err);
      } finally {
        setLoading(false);
      }
    } else {
      const fieldNames = {
        customer: t("common:customer"),
        contacts: t("common:contacts"),
        address: t("common:address"),
        // Nomme les champs obligatoires restés vides, le bloc en porte une quinzaine
    compInfos: compInfoFieldName(t),
        schedule: t("workorder:schedule"),
        time: t("workorder:time")
      };
      
      const errorMessage = validator.getFormErrorMessage(workorder, WorkorderFormValidator, fieldNames, t);
      toast.error(errorMessage);
    }
  };
  /***/

  /**
  * cancel: cancel workorder creation
  * reset form 
  */
  const cancel = () => {
    setWorkorder({} as EasyTech.Workorder);
    setSubmitted(false);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };
  /***/

  /**
  * handleTechnicianSelect: Handle technician selection from map
  * Close dialog after selection on mobile
  */
  const handleTechnicianSelect = (tech: EasyTech.Technician) => {
    setWorkorder({ ...workorder, technician: tech });
    
    if (isMobile) {
      setIsMapDialogOpen(false);
      toast.success(`${t("workorder:map:technician:selected")} ${tech.firstName} ${tech.lastName}`);
    }
  };
  /***/

  /**
  * openMapDialog: Open map dialog on mobile
  */
  const openMapDialog = () => {
    setIsMapDialogOpen(true);
  };
  /***/

  /**
  * closeMapDialog: Close map dialog
  */
  const closeMapDialog = () => {
    setIsMapDialogOpen(false);
  };
  /***/

  return (
    <div className="create-workorder">
      <Card className="wo-create-form">
        <Typography className="title">
          {t("workorder:create")}
        </Typography>

        <div className="body">
          <WorkorderForm  data={workorder}
                          onChange={handleChange}
                          onSubmit={() => null}
                          submitted={submitted}
                          onToggleShowUnavailable={setShowUnavailable}
                          onUnavailablesChange={setUnavailableTechs} />
        </div>

        <div className="footer">
          <div className="text-xs text-gray-400 italic flex-1">{t("common:mandatoryField")}</div>

          <Button
            variant="filled"
            color="white"
            className="btn-cancel normal-case font-thin border whitespace-nowrap"
            onClick={cancel}
          >
            {t("common:cancel")}
          </Button>

          <Button
            variant="filled"
            className="bg-blue-500 normal-case font-thin whitespace-nowrap flex items-center justify-center gap-2"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading && <Spinner className="h-4 w-4 text-white" />}
            {t("workorder:create")}
          </Button>
        </div>
      </Card>

      {!isMobile && (
        <WorkorderMap address={addressMap}
          onTechClick={handleTechnicianSelect}
          showUnavailable={showUnavailable}
          unavailableTechs={unavailableTechs} />
      )}

      {/* Bouton flottant pour ouvrir la carte sur mobile */}
      {isMobile && (
        <IconButton
          className="map-floating-btn"
          color="blue"
          size="lg"
          onClick={openMapDialog}
          title="Ouvrir la carte pour sélectionner un technicien"
          variant="filled"
        >
          <MapIcon className="w-8 h-8" />
        </IconButton>
      )}

      {/* Dialog pour la carte sur mobile */}
      <Dialog
        open={isMapDialogOpen && isMobile}
        handler={closeMapDialog}
        size="xl"
        className="mobile-map-dialog"
      >
        <DialogHeader className="flex items-center justify-between">
          <Typography variant="h5" color="blue-gray">
            {t("workorder:filters:technician:placeholder")}
          </Typography>
          <IconButton
            variant="text"
            color="blue-gray"
            onClick={closeMapDialog}
          >
            <XMarkIcon className="w-6 h-6" />
          </IconButton>
        </DialogHeader>
        <DialogBody className="p-0">
          <div className="mobile-map-container">
            <WorkorderMap address={addressMap}
              onTechClick={handleTechnicianSelect}
              showUnavailable={showUnavailable}
              unavailableTechs={unavailableTechs} />
          </div>
          {workorder.technician && (
            <div className="selected-tech-indicator">
              <Typography color="green" className="text-sm font-medium">
                {t("workorder:map:technician:selected")}: {workorder.technician.firstName} {workorder.technician.lastName}
              </Typography>
            </div>
          )}
        </DialogBody>
      </Dialog>
    </div>
  );
}
/***/
